import { Avatar, Box, Flex } from '@chakra-ui/react';
import { DotsLoader } from '@kula-ai/components';
import { KModal, KText } from 'ui-library/components';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { useShowToast } from '@/hooks';
import { CandidateRecords } from '@/components/ats/actions/delete/candidateRecords';
import { useAnonymizeCandidate, useContactRecords } from './api';
import { consentErrorMessage } from './consentStatus';

export interface AnonymizeCandidate {
  id: number;
  first_name?: string;
  last_name?: string;
  /** Slider / row payloads carry a single `name` instead of first/last. */
  name?: string;
  image_url?: string;
  projects?: unknown[];
  outreaches?: unknown[];
  ats_applications?: unknown[];
}

// The contact page passes a candidate that already holds its jobs/projects/flows; every other
// surface passes a thin contact, so the records have to be fetched before we can list them.
const hasRecords = (candidate: AnonymizeCandidate) =>
  !!(candidate.ats_applications || candidate.projects || candidate.outreaches);

const displayName = (candidate: AnonymizeCandidate) =>
  candidate.name ||
  `${candidate.first_name ?? ''} ${candidate.last_name ?? ''}`.trim();

interface AnonymizeCandidateModalProps {
  candidate: AnonymizeCandidate;
  modalActions: { isOpen: boolean; onClose: () => void };
  onSuccess?: () => void;
}

const DESCRIPTION =
  'Anonymizing a candidate permanently removes all their personal data — profile details, resumes, messages, and interview records. They stay linked to jobs, flows, and projects for reporting, but can no longer be viewed or edited. This cannot be undone.';

// Records-table anonymize confirm (mirrors DeleteCandidate) — no reason field.
export const AnonymizeCandidateModal = ({
  candidate,
  modalActions,
  onSuccess,
}: AnonymizeCandidateModalProps) => {
  const toast = useShowToast();
  const anonymize = useAnonymizeCandidate();
  const { id, image_url } = candidate;
  const needsRecords = !hasRecords(candidate);
  // A disabled query still reports isPending, so gate the loader on isFetching.
  const { data: fetched, isFetching } = useContactRecords(
    id,
    modalActions.isOpen && needsRecords
  );
  const isLoadingRecords = needsRecords && isFetching;
  const records = (needsRecords ? fetched : candidate) as AnonymizeCandidate;
  const name = displayName(candidate) || displayName(records ?? {});

  // The records table already surfaces active jobs/projects/flows, so clicking
  // confirm here IS the active-applications acknowledgement — send confirm
  // upfront rather than round-tripping a 422 for a second click.
  const handleConfirm = () =>
    anonymize.mutate(
      { target: { contactId: id }, confirm: true },
      {
        onSuccess: () => {
          toast({
            title: 'Candidate anonymized successfully',
            status: 'success',
          });
          onSuccess?.();
          modalActions.onClose();
        },
        onError: (err) =>
          toast({ title: consentErrorMessage(err), status: 'error' }),
      }
    );

  const title = (
    <Flex gap="6px" alignItems="center">
      <KText size="md-semi-bold" color="secondary-900">
        Are you sure you want to anonymize
      </KText>
      <Avatar
        size="xs"
        name={name}
        src={image_url}
        borderRadius="6px"
        w="20px"
        h="20px"
      />
      <TruncatedTextWithTooltip
        maxW="300px"
        size="md-semi-bold"
        toolTipVariant="default"
        textTransform="capitalize"
        text={`${name}?`}
        cursor="pointer"
      />
    </Flex>
  );

  return (
    <KModal
      size="2xl"
      isOpen={modalActions.isOpen}
      onClose={modalActions.onClose}
      ctaAction={handleConfirm}
      title={title}
      ctaLabel="Anonymize candidate"
      loadingText="Anonymizing"
      isCtaLoading={anonymize.isPending}
      ctaButtonProps={{ bg: 'error-200', _hover: { bg: 'error-200' } }}
      modalBodyProps={{ overflow: 'hidden' }}
    >
      <Flex pt={0} flexDir="column" h="full">
        <KText>{DESCRIPTION}</KText>
        <Box pt="24px">
          {isLoadingRecords ? (
            <Flex justify="center" py="40px">
              <DotsLoader />
            </Flex>
          ) : (
            <CandidateRecords candidate={records ?? candidate} />
          )}
        </Box>
      </Flex>
    </KModal>
  );
};
